/**
 * §4.6 — cuánto has reescrito de verdad: los tres niveles y el mapa por capítulo.
 *
 * Solo la vía B, porque solo ahí hay un manuscrito de partida contra el que medir. Los tres niveles
 * van SEPARADOS y sin un número que los resuma: la especificación lo prohíbe y, más importante, no
 * son la misma clase de cosa —frases que sobreviven, escenas movidas y decisiones sobre la historia
 * no se promedian—.
 *
 * Y el mapa por capítulo no es un adorno: una reescritura concentrada en tres capítulos tiene que
 * verse. Promediarla sobre el libro la esconde, que es lo contrario de documentarla.
 */
import { AMBITOS } from "../api";
import type { Profundidad as Datos } from "../api";

const pct = (x: number | null | undefined) => (x == null ? "—" : `${Math.round(x * 100)} %`);
const nombreAmbito = (id: string) => AMBITOS.find((a) => a.id === id)?.et ?? id;

export default function Profundidad({ d }: { d: Datos }) {
  if (!d.disponible) {
    return (
      <section className="carta">
        <h2>Cuánto has reescrito<span className="sub">no se puede medir</span></h2>
        <p className="vacio">{d.motivo}.</p>
      </section>
    );
  }
  const l = d.literal!;
  const e = d.estructural;
  const n = d.no_literal!;
  const nuevos = (d.capitulos ?? []).filter((c) => c.estado === "nuevo").length;
  const previos = (d.capitulos ?? []).length - nuevos;
  const ya = `${previos} ${previos === 1 ? "capítulo" : "capítulos"}`;
  return (
    <section className="carta">
      <h2>Cuánto has reescrito<span className="sub">tres niveles · no se promedian</span></h2>

      <div className="rejilla">
        <div className="cifra">
          <b>{pct(l.reescrito)}</b>
          <span>reescrito: de {l.frases_v0} frases de partida, siguen vivas {l.frases_vivas}</span>
        </div>
        <div className="cifra">
          <b>{e ? e.escenas_modificadas + e.escenas_anadidas + e.escenas_borradas : "—"}</b>
          <span>{e
            ? `escenas tocadas: ${e.escenas_anadidas} añadidas · ${e.escenas_borradas} cortadas · ${e.escenas_modificadas} modificadas`
            : `nivel estructural sin medir${d.motivo_estructural ? `: ${d.motivo_estructural}` : ""}`}</span>
        </div>
        <div className="cifra">
          <b>{n.declaradas.length}</b>
          <span>{n.sin_declarar
            ? "decisiones de fondo declaradas: ninguna todavía"
            : `decisiones de fondo: ${n.ambitos.map(nombreAmbito).join(" · ")}`}</span>
        </div>
      </div>

      <p className="vacio">
        {l.palabras_v0.toLocaleString("es-ES")} palabras en el manuscrito que sellaste →{" "}
        {l.palabras_ahora.toLocaleString("es-ES")} ahora
        {e ? ` · ${e.parrafos_reescritos.toLocaleString("es-ES")} párrafos reescritos, ${e.parrafos_anadidos.toLocaleString("es-ES")} añadidos, ${e.parrafos_cortados.toLocaleString("es-ES")} cortados` : ""}.
      </p>

      {n.sin_declarar ? (
        <p className="vacio">
          No has declarado ninguna decisión de fondo. Eso no significa que no hayas cambiado la trama,
          un personaje, el punto de vista, la cronología, el final o el tema: significa que no consta,
          y AWAP no lo deduce. Se marcan en el chat o se anotan aquí abajo.
        </p>
      ) : (
        <ul className="pasos">
          {n.declaradas.slice(0, 8).map((x, i) => (
            <li key={i}>
              <b>{x.ambitos.map(nombreAmbito).join(", ")}</b>{x.doc ? ` · ${x.doc}` : ""} · {x.origen}
              {x.nota ? ` — «${x.nota}»` : ""}
            </li>
          ))}
        </ul>
      )}

      {!!d.capitulos?.length && (
        <>
          <h2 style={{ marginTop: 18 }}>Por capítulo<span className="sub">dónde está la reescritura</span></h2>
          <div className="notas">
            {d.capitulos.map((c) => (
              <div className="nota" key={c.titulo}>
                <span className="et">{c.titulo}</span>
                <span className="raya">
                  <i style={{ width: `${Math.round((1 - (c.supervivencia ?? 0)) * 100)}%` }} />
                </span>
                <b className="mono">{c.estado}</b>
              </div>
            ))}
          </div>
          <p className="vacio">
            La barra es lo reescrito de cada capítulo. {ya} que ya {ya === "1 capítulo" ? "estaba" : "estaban"}
            {nuevos ? ` y ${nuevos} ${nuevos === 1 ? "nuevo" : "nuevos"}` : ""}.
          </p>
        </>
      )}

      <p className="vacio">
        Los tres niveles se leen por separado y ninguno entra en tu puntuación: miden qué has hecho
        con el manuscrito, no de quién es.
      </p>
    </section>
  );
}
